import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { Bar } from 'react-chartjs-2'
import 'chart.js/auto'

const API_TICKETS = 'https://garibcasinos.com/api/tickets.php'

function getAuth() {
  const raw = localStorage.getItem('auth')
  if (raw) {
    try {
      const a = JSON.parse(raw)
      return {
        usuario: a?.usuario || '',
        rol: String(a?.rol || '').toUpperCase(),
        maquinas: Array.isArray(a?.maquinas) ? a.maquinas : [],
      }
    } catch {}
  }
  // compatibilidad antigua
  const legacy = localStorage.getItem('usuario') || ''
  if (legacy === 'Admin') return { usuario: 'Admin', rol: 'ADMIN', maquinas: [] }
  return { usuario: legacy || '', rol: legacy ? 'COCINA' : '', maquinas: [] }
}

/** ===== Fechas robustas ===== */
function isYmd(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(s)
}

function ymdFromAny(value) {
  if (value === null || value === undefined) return ''

  // timestamp numérico (segundos o ms)
  if (typeof value === 'number') {
    const ms = value < 2_000_000_000 ? value * 1000 : value
    const d = new Date(ms)
    if (isNaN(d.getTime())) return ''
    return d.toISOString().slice(0, 10)
  }

  const s = String(value).trim()
  if (!s) return ''

  // "YYYY-MM-DD ..." o "YYYY-MM-DDTHH:MM..."
  if (s.length >= 10 && isYmd(s.slice(0, 10))) return s.slice(0, 10)

  // "DD/MM/YYYY" o "DD-MM-YYYY"
  const m = s.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})/)
  if (m) {
    const dd = m[1]
    const mm = m[2]
    const yyyy = m[3]
    return `${yyyy}-${mm}-${dd}`
  }

  // parse genérico
  const d = new Date(s)
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10)

  return ''
}

function ymdFromTicket(t) {
  const v =
    t?.Fecha ??
    t?.fecha ??
    t?.created_at ??
    t?.fecha_registro ??
    t?.fechaHora ??
    t?.fecha_hora ??
    t?.fecha_ticket ??
    t?.fechaTicket ??
    t?.timestamp ??
    t?.time ??
    t?.CreatedAt ??
    t?.createdAt ??
    ''

  return ymdFromAny(v)
}

function dateRange(fromYmd, toYmd) {
  if (!isYmd(fromYmd) || !isYmd(toYmd)) return []
  const out = []
  const from = new Date(fromYmd + 'T00:00:00')
  const to = new Date(toYmd + 'T00:00:00')
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return []
  if (from > to) return []
  const cur = new Date(from.getTime())
  while (cur <= to) {
    out.push(cur.toISOString().slice(0, 10))
    cur.setDate(cur.getDate() + 1)
  }
  return out
}

function clampLabels(arr, max = 62) {
  if (arr.length <= max) return arr
  return arr.slice(0, max)
}

const Tickets = () => {
  const obtenerFechaLocal = () => {
    const hoy = new Date()
    hoy.setMinutes(hoy.getMinutes() - hoy.getTimezoneOffset())
    return hoy.toISOString().slice(0, 10)
  }

  const auth = useMemo(() => getAuth(), [])
  const rol = auth.rol || 'COCINA'
  const isAdmin = rol === 'ADMIN'

  // ✅ Mapeo: ids de máquina -> string que trae tu tabla "Maquina"
  // Ajusta estos ids si tus maquinas en BD tienen otros números.
  const maquinaIdToKey = {
    1: 'baika_hijuelas',
    2: 'baika_sanfelipe',
  }

  const allowedMaquinaKeys = useMemo(() => {
    if (isAdmin) return ['baika_hijuelas', 'baika_sanfelipe']
    const keys = (auth.maquinas || [])
      .map((id) => maquinaIdToKey[Number(id)])
      .filter(Boolean)
    return keys
  }, [auth.maquinas, isAdmin])

  const [fechaDesde, setFechaDesde] = useState(obtenerFechaLocal())
  const [fechaHasta, setFechaHasta] = useState(obtenerFechaLocal())
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const obtenerTickets = async () => {
    setLoading(true)
    setError(null)
    try {
      if (!isAdmin && allowedMaquinaKeys.length === 0) {
        setTickets([])
        setError('No tienes máquinas asignadas. Pide a un admin que te asigne Hijuelas o San Felipe.')
        return
      }

      const params = {
        desde: fechaDesde,
        hasta: fechaHasta,
      }

      if (!isAdmin) {
        params.maquinas = allowedMaquinaKeys.join(',')
      }

      const res = await axios.get(API_TICKETS, { params })

      const data = res.data
      const rows = Array.isArray(data) ? data : (data?.tickets || [])

      const filtered = isAdmin
        ? rows
        : rows.filter((t) =>
            allowedMaquinaKeys.includes(String(t?.Maquina || t?.maquina || '').trim())
          )

      setTickets(filtered)
    } catch (err) {
      console.error(err)
      setError('Error cargando tickets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    obtenerTickets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta])

  const exportarExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(tickets)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tickets')

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' })
    saveAs(data, `tickets_${fechaDesde}_a_${fechaHasta}.xlsx`)
  }

  const maquinaKeyOf = (t) => {
    const v = t?.Maquina ?? t?.maquina
    return v ? String(v).trim() : 'Sin máquina'
  }

  const almuerzoKeyOf = (t) => {
    const v = t?.almuerzo ?? t?.Almuerzo
    if (v === null || v === undefined) return 'No seleccionado'
    const s = String(v).trim()
    return s.length ? s : 'No seleccionado'
  }

  const areaKeyOf = (t) => {
    const v =
      t?.AreaTrabajo ??
      t?.area ??
      t?.Area ??
      t?.['Trabajo - Nombre Área'] ??
      t?.['Trabajo - Nombre Área '] ??
      t?.trabajo_area ??
      ''
    const s = String(v || '').trim()
    return s.length ? s : 'Sin área'
  }

  // =========================
  // Totales
  // =========================
  const totalTickets = tickets.length
  const totalHijuelas = tickets.filter((t) => maquinaKeyOf(t) === 'baika_hijuelas').length
  const totalSanFelipe = tickets.filter((t) => maquinaKeyOf(t) === 'baika_sanfelipe').length

  // =========================
  // Tickets por máquina
  // =========================
  const ticketsPorMaquina = useMemo(() => {
    return tickets.reduce((acc, t) => {
      const key = maquinaKeyOf(t)
      acc[key] = (acc[key] || 0) + 1
      return acc
    }, {})
  }, [tickets])

  const ordenMaquinas = ['baika_hijuelas', 'baika_sanfelipe']
  const etiquetasMaquina = {
    baika_hijuelas: 'Hijuelas',
    baika_sanfelipe: 'San Felipe',
  }

  const maquinasOrdenadas = useMemo(() => {
    // mostrar sólo las permitidas por perfil
    const allowed = isAdmin ? ordenMaquinas : allowedMaquinaKeys
    return ordenMaquinas.filter((m) => allowed.includes(m) && ticketsPorMaquina[m])
  }, [ticketsPorMaquina, isAdmin, allowedMaquinaKeys])

  const dataMaquinas = useMemo(() => {
    return {
      labels: maquinasOrdenadas.map((m) => etiquetasMaquina[m] || m),
      datasets: [
        {
          label: 'Tickets',
          data: maquinasOrdenadas.map((m) => ticketsPorMaquina[m] || 0),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
        },
      ],
    }
  }, [maquinasOrdenadas, ticketsPorMaquina])

  // =========================
  // Almuerzos más solicitados
  // =========================
  const ticketsPorAlmuerzo = useMemo(() => {
    return tickets.reduce((acc, t) => {
      const key = almuerzoKeyOf(t)
      acc[key] = (acc[key] || 0) + 1
      return acc
    }, {})
  }, [tickets])

  const almuerzosOrdenados = useMemo(() => {
    return Object.keys(ticketsPorAlmuerzo).sort(
      (a, b) => ticketsPorAlmuerzo[b] - ticketsPorAlmuerzo[a]
    )
  }, [ticketsPorAlmuerzo])

  const dataAlmuerzos = useMemo(() => {
    return {
      labels: almuerzosOrdenados,
      datasets: [
        {
          label: 'Almuerzos más solicitados',
          data: almuerzosOrdenados.map((k) => ticketsPorAlmuerzo[k]),
          backgroundColor: 'rgba(255, 159, 64, 0.6)',
        },
      ],
    }
  }, [almuerzosOrdenados, ticketsPorAlmuerzo])

  // =========================
  // ✅ Tickets por día (robusto)
  // =========================
  const labelsDias = useMemo(() => {
    return clampLabels(dateRange(fechaDesde, fechaHasta), 62)
  }, [fechaDesde, fechaHasta])

  const diasRango = labelsDias.length

  const ticketsCountByDay = useMemo(() => {
    const acc = {}
    labelsDias.forEach((d) => (acc[d] = 0))

    tickets.forEach((t) => {
      const d = ymdFromTicket(t)
      if (!d) return
      if (typeof acc[d] === 'number') acc[d] += 1
    })

    return acc
  }, [tickets, labelsDias])

  const dataTicketsPorDia = useMemo(() => {
    return {
      labels: labelsDias,
      datasets: [
        {
          label: 'Tickets por día',
          data: labelsDias.map((d) => ticketsCountByDay[d] || 0),
          backgroundColor: 'rgba(29, 209, 161, 0.6)',
        },
      ],
    }
  }, [labelsDias, ticketsCountByDay])

  // =========================
  // ✅ Por día y por máquina (stacked) respetando perfil
  // =========================
  const dataTicketsPorDiaMaquina = useMemo(() => {
    const acc = {}
    labelsDias.forEach((d) => {
      acc[d] = { baika_hijuelas: 0, baika_sanfelipe: 0 }
    })

    tickets.forEach((t) => {
      const d = ymdFromTicket(t)
      if (!d || !acc[d]) return
      const m = maquinaKeyOf(t)
      if (m === 'baika_hijuelas' || m === 'baika_sanfelipe') acc[d][m] += 1
    })

    const allowed = isAdmin ? ['baika_hijuelas', 'baika_sanfelipe'] : allowedMaquinaKeys
    const datasets = []

    if (allowed.includes('baika_hijuelas')) {
      datasets.push({
        label: 'Hijuelas',
        data: labelsDias.map((d) => acc[d]?.baika_hijuelas || 0),
        backgroundColor: 'rgba(54, 162, 235, 0.65)',
      })
    }

    if (allowed.includes('baika_sanfelipe')) {
      datasets.push({
        label: 'San Felipe',
        data: labelsDias.map((d) => acc[d]?.baika_sanfelipe || 0),
        backgroundColor: 'rgba(255, 159, 64, 0.65)',
      })
    }

    return { labels: labelsDias, datasets }
  }, [labelsDias, tickets, isAdmin, allowedMaquinaKeys])

  const stackedOptions = useMemo(
    () => ({
      responsive: true,
      plugins: { legend: { position: 'top' } },
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true },
      },
    }),
    []
  )

  // =========================
  // ✅ Por área y por máquina (stacked) respetando perfil
  // =========================
  const dataPorAreaMaquina = useMemo(() => {
    const matrix = {}
    const allowed = isAdmin ? ['baika_hijuelas', 'baika_sanfelipe'] : allowedMaquinaKeys

    tickets.forEach((t) => {
      const a = areaKeyOf(t)
      const m = maquinaKeyOf(t)
      if (!allowed.includes(m)) return
      if (!matrix[a]) matrix[a] = {}
      matrix[a][m] = (matrix[a][m] || 0) + 1
    })

    const areasOrdenadas = Object.keys(matrix)
      .sort((a, b) => {
        const ta = Object.values(matrix[a]).reduce((s, n) => s + Number(n || 0), 0)
        const tb = Object.values(matrix[b]).reduce((s, n) => s + Number(n || 0), 0)
        return tb - ta
      })
      .slice(0, 15)

    const datasets = []

    if (allowed.includes('baika_hijuelas')) {
      datasets.push({
        label: 'Hijuelas',
        data: areasOrdenadas.map((a) => matrix[a]?.baika_hijuelas || 0),
        backgroundColor: 'rgba(54, 162, 235, 0.65)',
      })
    }

    if (allowed.includes('baika_sanfelipe')) {
      datasets.push({
        label: 'San Felipe',
        data: areasOrdenadas.map((a) => matrix[a]?.baika_sanfelipe || 0),
        backgroundColor: 'rgba(255, 159, 64, 0.65)',
      })
    }

    return {
      labels: areasOrdenadas,
      datasets,
    }
  }, [tickets, isAdmin, allowedMaquinaKeys])

  return (
    <section className="section">
      <div className="container">
        <div className="is-flex is-justify-content-space-between is-align-items-center">
          <div>
            <h1 className="title">Tickets</h1>
            <p className="has-text-grey">
              Rol: <b>{rol}</b>{' '}
              {!isAdmin && (
                <>
                  · Máquinas: <b>{allowedMaquinaKeys.map((k) => etiquetasMaquina[k] || k).join(', ')}</b>
                </>
              )}
            </p>
          </div>

          <button className={`button is-light ${loading ? 'is-loading' : ''}`} onClick={obtenerTickets}>
            🔄 Refrescar
          </button>
        </div>

        {error && <div className="notification is-danger is-light">{error}</div>}

        <div className="columns">
          <div className="column">
            <label className="label">Desde:</label>
            <input type="date" className="input" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
          </div>

          <div className="column">
            <label className="label">Hasta:</label>
            <input type="date" className="input" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
          </div>

          <div className="column is-narrow">
            <label className="label">&nbsp;</label>
            <button className="button is-info" onClick={exportarExcel} disabled={tickets.length === 0}>
              📁 Exportar a Excel
            </button>
          </div>
        </div>

        <div className="notification is-primary">
          <strong>Total de Tickets:</strong> {totalTickets}
        </div>

        {(isAdmin || allowedMaquinaKeys.length > 1) && (
          <div className="columns">
            <div className="column">
              <div className="notification is-link">
                <strong>Hijuelas:</strong> {totalHijuelas}
              </div>
            </div>
            <div className="column">
              <div className="notification is-info">
                <strong>San Felipe:</strong> {totalSanFelipe}
              </div>
            </div>
          </div>
        )}

        <div className="box">
          <h2 className="subtitle">Tickets por Planta</h2>
          <Bar data={dataMaquinas} />
        </div>

        <div className="box">
          <h2 className="subtitle">Almuerzos más solicitados</h2>
          <Bar data={dataAlmuerzos} />
        </div>

        {diasRango > 1 && (
          <>
            <div className="box">
              <h2 className="subtitle">Tickets por día</h2>
              <Bar
                data={dataTicketsPorDia}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'top' } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
              <p className="has-text-grey is-size-7">
                * Si sale todo en 0: tu API no está devolviendo la fecha o viene con otro nombre/formato.
              </p>
            </div>

            <div className="box">
              <h2 className="subtitle">Tickets por día y por planta</h2>
              <Bar data={dataTicketsPorDiaMaquina} options={stackedOptions} />
            </div>

            <div className="box">
              <h2 className="subtitle">Tickets por Área y por planta (Top 15 áreas)</h2>
              <Bar data={dataPorAreaMaquina} options={stackedOptions} />
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export default Tickets